// A store's owner (or any teammate) as iOS draws them: InitialsAvatar on the user's color, plus the presence dot from
// Team+Store.swift (Online < 15 min, Away < 8 h) when a position is known. Optionally the name beside it, and the
// AvatarStack "Unassigned" look when there is no owner.

import { UserRoundX } from 'lucide-react';
import type { UserPayload } from '@/contracts/models';
import { PRESENCE_TITLES, type Presence } from '@/domain/team';
import { fullName } from '@/domain/util';
import { Avatar, avatarPixels, cn, type AvatarSize } from '@/ui';

export interface OwnerAvatarProps {
  user: Pick<UserPayload, 'firstName' | 'lastName' | 'colorHex' | 'isActive'> | null | undefined;
  /** Live presence (`presenceOf(position.at, now)`); omit for no dot. */
  presence?: Presence | null;
  /** 20 px (`xs`) by default, the size RecordRow uses. */
  size?: AvatarSize | number;
  /** Prints the name next to the avatar (table cells, pickers). */
  showName?: boolean;
  /** Spoken before the name: "Owner Jane Doe". */
  role?: string;
  /** Text for no user ("Unassigned"); null renders nothing. */
  unassignedLabel?: string | null;
  className?: string;
}

export function OwnerAvatar({
  user,
  presence,
  size = 'xs',
  showName = false,
  role,
  unassignedLabel = 'Unassigned',
  className,
}: OwnerAvatarProps) {
  const px = avatarPixels(size);
  if (!user) {
    if (unassignedLabel === null) return null;
    const icon = <UserRoundX aria-hidden className="shrink-0 text-grey" style={{ width: px * 0.8, height: px * 0.8 }} />;
    if (!showName) {
      return (
        <span role="img" aria-label={unassignedLabel} title={unassignedLabel} className={cn('inline-flex shrink-0', className)}>
          {icon}
        </span>
      );
    }
    return (
      <span className={cn('inline-flex min-w-0 items-center gap-1.5 text-fg-muted', className)}>
        {icon}
        <span className="truncate">{unassignedLabel}</span>
      </span>
    );
  }

  const name = fullName(user) || 'Unnamed';
  const status = [presence ? PRESENCE_TITLES[presence] : null, user.isActive ? null : 'Inactive'].filter(Boolean).join(', ');
  const spoken = [role ? `${role} ${name}` : name, status].filter(Boolean).join(', ');

  if (!showName) {
    return <Avatar name={name} colorHex={user.colorHex} size={size} presence={presence ?? null} label={spoken} className={className} />;
  }
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5', className)} title={spoken}>
      <Avatar name={name} colorHex={user.colorHex} size={size} presence={presence ?? null} label={null} />
      <span className={cn('truncate', user.isActive ? 'text-fg' : 'text-fg-muted')}>
        {role && <span className="sr-only">{role} </span>}
        {name}
        {!user.isActive && <span className="text-fg-subtle"> · Inactive</span>}
        {presence && <span className="sr-only">, {PRESENCE_TITLES[presence]}</span>}
      </span>
    </span>
  );
}
